/**
 * =============================================================================
 * Componente: QuickActionsRow
 * Versão: 2.1.0
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-23 v2.1.0 - Corrige bug "/r/null" ao Copiar/WhatsApp/QR na
 *            home quando o afiliado ainda não gerou o link pessoal daquela
 *            campanha. Passa a usar `useEnsureCampaignLink` (mesmo padrão do
 *            CampaignCard) antes de qualquer ação, com loader por ação
 *            individual (pendingAction).
 *   RELEASE: 2026-xx-xx v2.0.0 - Multi-campanha com seletor inline.
 *
 * Descrição:
 *   Faixa horizontal de ações rápidas (Copiar / WhatsApp / QR) ancoradas à
 *   "campanha ativa" do afiliado. Se o link pessoal ainda não existir, ele
 *   é criado sob demanda via RPC `get_or_create_campaign_link` antes de
 *   copiar/compartilhar/gerar QR.
 * =============================================================================
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Copy,
  Check,
  Share2,
  QrCode,
  Megaphone,
  Loader2,
  ChevronDown,
  ArrowRight,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAffiliateLinkActions } from '@/hooks/useAffiliateLinkActions';
import { QrCodeDialog } from '@/components/affiliate/QrCodeDialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  useEnsureCampaignLink,
  type MyCampaignLink,
} from '@/hooks/useAffiliateMyCampaigns';


interface QuickActionsRowProps {
  /**
   * Lista completa de campanhas do afiliado (já carregadas pelo dashboard).
   * Quando length > 1 o seletor inline aparece acima dos botões.
   */
  campaigns?: MyCampaignLink[];
  /** Campanha selecionada (vinda do hook useSelectedAffiliateCampaign). */
  selectedCampaign?: MyCampaignLink;
  /** Handler para troca de campanha pelo seletor. */
  onSelectCampaign?: (campaignId: string) => void;
}

/** Monta a URL pública /r/<short_code|slug> a partir do origin atual. */
function buildPublicUrl(slug: string | null, shortCode: string | null): string {
  const code = shortCode ?? slug ?? '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin.replace(/\/$/, '')}/r/${code}`;
}

/** Snapshot mínimo do link resolvido (existente ou recém-criado). */
type ResolvedLink = {
  id: string;
  slug: string;
  short_code: string | null;
};

export function QuickActionsRow({
  campaigns,
  selectedCampaign,
  onSelectCampaign,
}: QuickActionsRowProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Ação em andamento (mostra loader individual e desabilita as outras).
  const [pendingAction, setPendingAction] = useState<
    'copy' | 'share' | 'qr' | null
  >(null);
  // Link resolvido para a campanha selecionada — usado como fonte
  // confiável para o QrCodeDialog (evita depender do cache assíncrono).
  const [resolvedLink, setResolvedLink] = useState<ResolvedLink | null>(null);
  const { shareWhatsapp, isSharingWhatsapp } = useAffiliateLinkActions();
  const ensureLink = useEnsureCampaignLink();

  const hasMultiple = (campaigns?.length ?? 0) > 1;
  const activeName = selectedCampaign?.campaign_name ?? '';

  // ---------------------------------------------------------------------------
  // Lista exibida no popover:
  // - Mantém a ORDEM do backend (sem ordenar por cliques/indicações/etc).
  // - Apenas eleva a campanha selecionada para o topo, para feedback rápido.
  // - Aplica filtro case-insensitive pelo nome quando há texto na busca.
  // ---------------------------------------------------------------------------
  const filteredCampaigns = useMemo(() => {
    const list = campaigns ?? [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? list.filter((c) => c.campaign_name.toLowerCase().includes(term))
      : list;
    if (!selectedCampaign) return filtered;
    // Estabiliza topo: selecionada primeiro, demais na ordem original do backend.
    return [...filtered].sort((a, b) => {
      const aSel = a.campaign_id === selectedCampaign.campaign_id ? -1 : 0;
      const bSel = b.campaign_id === selectedCampaign.campaign_id ? -1 : 0;
      return aSel - bSel;
    });
  }, [campaigns, search, selectedCampaign]);

  const totalCampaigns = campaigns?.length ?? 0;

  // ---------------------------------------------------------------------------
  // Resolve o link pessoal da campanha selecionada.
  // - Se já existir (link_id + slug/short_code), reaproveita sem chamar RPC.
  // - Caso contrário, cria on-demand via `useEnsureCampaignLink` (mesmo
  //   padrão do CampaignCard). Corrige o bug histórico de "/r/null" quando
  //   o afiliado nunca havia interagido com a campanha antes.
  // ---------------------------------------------------------------------------
  const resolveLink = async (
    campaign: MyCampaignLink,
  ): Promise<ResolvedLink | null> => {
    if (campaign.link_id && (campaign.short_code || campaign.slug)) {
      const existing: ResolvedLink = {
        id: campaign.link_id,
        slug: campaign.slug ?? '',
        short_code: campaign.short_code,
      };
      setResolvedLink(existing);
      return existing;
    }
    try {
      const link = await ensureLink.mutateAsync({
        campaignId: campaign.campaign_id,
      });
      const created: ResolvedLink = {
        id: link.id,
        slug: link.slug,
        short_code: link.short_code,
      };
      setResolvedLink(created);
      return created;
    } catch {
      toast.error('Não foi possível preparar seu link pessoal.');
      return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Handlers das 3 ações — todos sensíveis à campanha selecionada.
  // Toast nomeia a campanha para deixar claro qual link foi acionado.
  // ---------------------------------------------------------------------------
  const handleCopy = async () => {
    if (!selectedCampaign) {
      navigate('/afiliado/links');
      return;
    }
    setPendingAction('copy');
    try {
      const link = await resolveLink(selectedCampaign);
      if (!link) return;
      await navigator.clipboard.writeText(
        buildPublicUrl(link.slug, link.short_code),
      );
      setCopied(true);
      toast.success(`Link de "${activeName}" copiado!`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Não foi possível copiar.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleShare = async () => {
    if (!selectedCampaign) {
      navigate('/afiliado/links');
      return;
    }
    setPendingAction('share');
    try {
      const link = await resolveLink(selectedCampaign);
      if (!link) return;
      const res = await shareWhatsapp({ linkId: link.id });
      window.open(res.whatsapp_url, '_blank', 'noopener,noreferrer');
      toast.success(`Compartilhando "${activeName}" no WhatsApp`);
    } catch {
      toast.error('Não foi possível abrir o WhatsApp.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleQr = async () => {
    if (!selectedCampaign) {
      navigate('/afiliado/links');
      return;
    }
    setPendingAction('qr');
    try {
      const link = await resolveLink(selectedCampaign);
      if (!link) return;
      setQrOpen(true);
    } finally {
      setPendingAction(null);
    }
  };



  // ---------------------------------------------------------------------------
  // Estado vazio: nenhuma campanha disponível
  // ---------------------------------------------------------------------------
  if (!selectedCampaign) {
    return (
      <section aria-label="Ações rápidas" className="grid grid-cols-1">
        <button
          type="button"
          onClick={() => navigate('/afiliado/links')}
          className={cn(
            'aff-card flex items-center gap-3 px-4 py-4 text-left transition-transform active:scale-[0.99]',
          )}
        >
          <span
            className="flex h-12 w-12 items-center justify-center rounded-2xl text-[hsl(var(--aff-primary-foreground))]"
            style={{ background: 'var(--aff-gradient-hero)' }}
          >
            <Megaphone className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[hsl(var(--aff-foreground))]">
              Ver campanhas disponíveis
            </span>
            <span className="block text-[11.5px] text-[hsl(var(--aff-muted-foreground))]">
              Pegue seu link pessoal e comece a divulgar
            </span>
          </span>
        </button>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Layout normal — 3 ações + (opcional) seletor de campanha
  // ---------------------------------------------------------------------------
  return (
    <>
      <div className="flex flex-col gap-2 md:gap-3 md:h-full">
        {/* Seletor de campanha — só aparece com 2+ campanhas */}
        {hasMultiple && (
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Trocar campanha ativa (atual: ${activeName})`}
                className={cn(
                  'flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-all',
                  'bg-[hsl(var(--aff-surface))] border-[hsl(var(--aff-border))] shadow-sm',
                  'hover:shadow-md',
                )}
              >
                {/*
                  Badge do ícone — usa o gradiente personalizado do tema
                  do afiliado (mesma linguagem visual do HeroBalanceCard
                  e do estado vazio "Ver campanhas disponíveis").
                */}
                <span
                  aria-hidden
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg shadow-sm text-[hsl(var(--aff-primary-foreground))]"
                  style={{ background: 'var(--aff-gradient-hero)' }}
                >
                  <Megaphone className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[10.5px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--aff-muted-foreground))]">
                    Campanha ativa
                  </span>
                  <span className="block truncate text-[13px] font-semibold text-[hsl(var(--aff-foreground))]">
                    {activeName}
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-[hsl(var(--aff-muted-foreground))]" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              onOpenAutoFocus={(e) => {
                // Evita que o input de busca roube foco e dispare scroll;
                // usuário pode focar manualmente quando precisar buscar.
                e.preventDefault();
              }}
              className="flex w-[min(360px,calc(100vw-2rem))] flex-col p-0 overflow-hidden"
            >
              {/*
                Header fixo do popover:
                - Mostra o total de campanhas ativas para dar noção de volume.
                - Campo de busca SEMPRE visível (filtro client-side por nome).
              */}
              <div className="flex flex-col gap-2 border-b border-[hsl(var(--aff-border))] px-2 pb-2 pt-2">
                <div className="flex items-center justify-between px-1">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--aff-muted-foreground))]">
                    Campanhas ativas
                  </span>
                  <span className="rounded-full bg-[hsl(var(--aff-primary-soft))] px-1.5 py-0.5 text-[10.5px] font-semibold text-[hsl(var(--aff-primary))]">
                    {totalCampaigns}
                  </span>
                </div>
                <div className="relative">
                  <Search
                    aria-hidden
                    className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[hsl(var(--aff-muted-foreground))]"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar campanha…"
                    aria-label="Buscar campanha pelo nome"
                    className={cn(
                      'h-8 w-full rounded-md border bg-[hsl(var(--aff-surface))] pl-7 pr-7 text-[12.5px]',
                      'border-[hsl(var(--aff-border))] text-[hsl(var(--aff-foreground))] placeholder:text-[hsl(var(--aff-muted-foreground))]',
                      'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--aff-primary))]/30',
                    )}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      aria-label="Limpar busca"
                      className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[hsl(var(--aff-muted-foreground))] hover:bg-[hsl(var(--aff-surface-2))]"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              {/*
                Lista scrollável — altura responsiva:
                - Cresce até 60vh em telas grandes (mais aproveitamento).
                - Limitada a 360px para não invadir a viewport.
                - Estado vazio quando o filtro não casa.
              */}
              <ul className="max-h-[min(60vh,360px)] overflow-y-auto p-1">
                {filteredCampaigns.length === 0 ? (
                  <li className="px-3 py-6 text-center text-[12px] text-[hsl(var(--aff-muted-foreground))]">
                    Nenhuma campanha encontrada para
                    <span className="ml-1 font-semibold text-[hsl(var(--aff-foreground))]">
                      "{search}"
                    </span>
                  </li>
                ) : (
                  filteredCampaigns.map((c) => {
                    const isActive = c.campaign_id === selectedCampaign.campaign_id;
                    return (
                      <li key={c.campaign_id}>
                        <button
                          type="button"
                          onClick={() => {
                            onSelectCampaign?.(c.campaign_id);
                            setPickerOpen(false);
                            setSearch('');
                          }}
                          className={cn(
                            'flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left transition-colors',
                            isActive
                              ? 'bg-[hsl(var(--aff-primary-soft))]'
                              : 'hover:bg-[hsl(var(--aff-surface-2))]',
                          )}
                        >
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                            {isActive && (
                              <Check className="h-4 w-4 text-[hsl(var(--aff-primary))]" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-semibold text-[hsl(var(--aff-foreground))]">
                              {c.campaign_name}
                            </span>
                            <span className="block truncate text-[11.5px] text-[hsl(var(--aff-muted-foreground))]">
                              {c.clicks_count} cliques · {c.referrals_count} indicações
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              {/* Footer fixo — atalho para a página completa de campanhas */}
              <div className="border-t border-[hsl(var(--aff-border))] p-1">
                <button
                  type="button"
                  onClick={() => {
                    setPickerOpen(false);
                    setSearch('');
                    navigate('/afiliado/links');
                  }}
                  className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-[12.5px] font-medium text-[hsl(var(--aff-primary))] transition-colors hover:bg-[hsl(var(--aff-primary-soft))]"
                >
                  Gerenciar campanhas
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/*
          Container das ações rápidas.
          - Mobile: linha simples (grid 3 cols, pílulas compactas)
          - Desktop: ocupa toda altura disponível (flex-1 quando seletor presente,
            h-full quando único filho) para ficar lado a lado com o Funil 3D
            no layout 1/2 + 1/2 da home.
        */}
        <section
          aria-label="Ações rápidas"
          className={cn(
            'grid grid-cols-3 gap-2 md:gap-3',
            // Sem seletor: ocupa toda a altura da coluna (comportamento legacy v1.0)
            // Com seletor: cresce ocupando o restante do espaço da coluna
            hasMultiple ? 'md:flex-1 md:min-h-0' : 'md:h-full',
          )}
        >
          <ActionPill
            icon={
              pendingAction === 'copy' ? (
                <Loader2 className="h-5 w-5 md:h-6 md:w-6 animate-spin" />
              ) : copied ? (
                <Check className="h-5 w-5 md:h-6 md:w-6" />
              ) : (
                <Copy className="h-5 w-5 md:h-6 md:w-6" />
              )
            }
            label={copied ? 'Copiado' : 'Compartilhar link'}
            onClick={handleCopy}
            disabled={pendingAction !== null && pendingAction !== 'copy'}
            ariaLabel={`Compartilhar link da campanha ${activeName}`}

          />
          <ActionPill
            icon={
              isSharingWhatsapp || pendingAction === 'share' ? (
                <Loader2 className="h-5 w-5 md:h-6 md:w-6 animate-spin" />
              ) : (
                <Share2 className="h-5 w-5 md:h-6 md:w-6" />
              )
            }
            label="WhatsApp"
            onClick={handleShare}
            disabled={
              isSharingWhatsapp ||
              (pendingAction !== null && pendingAction !== 'share')
            }
            ariaLabel={`Compartilhar campanha ${activeName} no WhatsApp`}
          />
          <ActionPill
            icon={
              pendingAction === 'qr' ? (
                <Loader2 className="h-5 w-5 md:h-6 md:w-6 animate-spin" />
              ) : (
                <QrCode className="h-5 w-5 md:h-6 md:w-6" />
              )
            }
            label="QR Code"
            onClick={handleQr}
            disabled={pendingAction !== null && pendingAction !== 'qr'}
            ariaLabel={`Abrir QR Code da campanha ${activeName}`}
          />
        </section>
      </div>

      <QrCodeDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        linkId={resolvedLink?.id ?? selectedCampaign.link_id}
        downloadFileName={
          resolvedLink?.short_code ??
          resolvedLink?.slug ??
          selectedCampaign.short_code ??
          selectedCampaign.slug
        }
      />

    </>
  );
}

interface ActionPillProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}

function ActionPill({ icon, label, onClick, disabled, ariaLabel }: ActionPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      className={cn(
        // Mobile: pílula compacta. Desktop: cresce verticalmente (h-full)
        // para casar com a altura do card de Funil ao lado.
        'flex flex-col items-center justify-center gap-1.5 md:gap-3 py-3.5 md:py-6 rounded-2xl md:h-full',
        'bg-[hsl(var(--aff-surface))] border border-[hsl(var(--aff-border))] shadow-sm',
        // Hover unificado com os demais KPIs da Home: elevação + sombra,
        // sem alterar a cor da borda.
        'transition-all duration-200 active:scale-95 hover:shadow-md hover:-translate-y-0.5',
        'disabled:opacity-60 disabled:active:scale-100',
      )}
    >
      <span className="flex h-11 w-11 md:h-14 md:w-14 items-center justify-center rounded-full text-[hsl(var(--aff-primary))] bg-[hsl(var(--aff-primary-soft))]">
        {icon}
      </span>
      <span className="text-[11.5px] md:text-[13px] font-semibold text-[hsl(var(--aff-foreground))]">
        {label}
      </span>
    </button>
  );
}
